import {
  carePlanDraftSchema,
  type CarePlanActivation,
  type CarePlanDraftRequest,
  type CarePlanVersionView,
  type CarePlanView,
  type CareTask,
  type CareTaskList,
} from '@homecare/contracts';
import { activationProblem, diffVersions, toLocalDate, type VersionDiff } from '@homecare/domain';
import {
  Button,
  Card,
  EmptyState,
  Eyebrow,
  Field,
  Notice,
  SelectField,
  Spinner,
  StatusPill,
  type StatusTone,
} from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { dayText, instantText } from '../../lib/schedule-time';
import { useHasPermission, useSession } from '../../lib/session';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';

/**
 * The client's care plan (§13.1, slice-03 exit criterion 1).
 *
 * What is in force today first, because it is what caregivers are doing; the
 * draft, if any, next, because it is what someone is working on; then every
 * version, newest first, each with what changed from the one before — §13.1's
 * diff, which "supervisors and auditors both ask for".
 *
 * Standings ("in force", "upcoming") come from the API, which derives them
 * with the same `versionInForce` the booking uses, so this tab and the tasks
 * on a visit cannot disagree. The activation check runs here too, through the
 * domain's `activationProblem`, so the button says why it will refuse before
 * it is pressed — and the API runs it again, because the button is advice.
 */

const STANDING: Record<CarePlanVersionView['standing'], { tone: StatusTone; label: string }> = {
  IN_FORCE: { tone: 'verified', label: 'In force' },
  UPCOMING: { tone: 'scheduled', label: 'Upcoming' },
  DRAFT: { tone: 'warning', label: 'Draft' },
  SUPERSEDED: { tone: 'inactive', label: 'Superseded' },
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const daysText = (days: readonly number[]): string =>
  days.length === 0 || days.length === 7
    ? 'every visit'
    : [...days]
        .sort()
        .map((day) => DAY_NAMES[day])
        .join(', ');

export function CarePlanPanel({ clientId }: { clientId: string }): ReactNode {
  const api = useApi();
  const canRead = useHasPermission('careplan:read');
  const canCreate = useHasPermission('careplan:create');
  const [failure, setFailure] = useState<unknown>(null);
  const [starting, setStarting] = useState(false);
  const queryClient = useQueryClient();

  const plan = useQuery({
    queryKey: ['clients', 'care-plan', clientId],
    queryFn: () => api.get<CarePlanView>(`/v1/clients/${clientId}/care-plan`),
    enabled: canRead,
  });
  const library = useQuery({
    queryKey: ['care-tasks'],
    queryFn: () => api.get<CareTaskList>('/v1/care-tasks'),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
  });

  if (!canRead) {
    return (
      <Card>
        <EmptyState
          title="The care plan is kept by care managers"
          explanation="Reading a client's care plan needs the careplan:read permission. A care manager or an admin can see it, and the tasks for each visit appear on the visit itself."
        />
      </Card>
    );
  }

  const data = plan.data;
  const refresh = (next: CarePlanView): void => {
    queryClient.setQueryData(['clients', 'care-plan', clientId], next);
    void queryClient.invalidateQueries({ queryKey: ['schedule'] });
  };

  /** A new draft: empty, or a revision pre-filled from the version given. */
  const startDraft = async (from: CarePlanVersionView | null): Promise<void> => {
    if (!data) return;
    setStarting(true);
    setFailure(null);
    try {
      refresh(
        await api.send<CarePlanView>('POST', `/v1/clients/${clientId}/care-plan/drafts`, {
          effectiveFrom: data.today,
          goals:
            from?.goals.map((goal) => ({ text: goal.text, targetDate: goal.targetDate })) ?? [],
          precautions: from?.precautions ?? null,
          tasks:
            from?.tasks.map((task) => ({
              taskLibraryId: task.taskLibraryId,
              daysOfWeek: task.daysOfWeek,
              serviceCodeId: task.serviceCodeId,
              instructions: task.instructions,
              isRequired: task.isRequired,
            })) ?? [],
          revisesVersionId: from?.id ?? null,
        }),
      );
    } catch (error) {
      setFailure(error);
    } finally {
      setStarting(false);
    }
  };

  if (plan.isPending) {
    return (
      <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
        <Spinner /> Loading the care plan…
      </p>
    );
  }
  if (plan.isError || !data) {
    return <ErrorNote error={plan.error} nextStep="Reload the page to try again." />;
  }

  const draft = data.versions.find((version) => version.standing === 'DRAFT') ?? null;
  const inForce = data.versions.find((version) => version.standing === 'IN_FORCE') ?? null;
  const upcoming = data.versions.find((version) => version.standing === 'UPCOMING') ?? null;
  const latestActivated = upcoming ?? inForce;

  return (
    <div className="grid gap-5" data-testid="care-plan">
      <ErrorNote error={failure} nextStep="Reload the page, then try again." />

      {data.versions.length === 0 ? (
        <Card>
          <EmptyState
            title="No care plan yet"
            explanation="The tasks a caregiver follows on each visit — personal care, meals, medication reminders — chosen from the task library, with goals and precautions. Visits booked once it is in force carry its tasks."
          />
          {canCreate && (
            <div className="mt-4 flex justify-center">
              <Button pending={starting} onClick={() => void startDraft(null)}>
                Start a care plan
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <>
          {!inForce && (
            <Notice tone="warning" title="Nothing is in force today">
              {upcoming
                ? `Version ${upcoming.versionNumber} starts on ${dayText(upcoming.effectiveFrom)}. Visits before then carry no tasks.`
                : 'Visits booked now carry no tasks. Activate the draft to put the plan in force.'}
            </Notice>
          )}

          {draft && (
            <DraftEditor
              key={draft.id}
              draft={draft}
              today={data.today}
              previous={latestActivated}
              library={library.data?.tasks ?? []}
              onSaved={refresh}
            />
          )}

          {!draft && canCreate && latestActivated && (
            <div className="flex justify-end">
              <Button
                variant="secondary"
                pending={starting}
                onClick={() => void startDraft(latestActivated)}
              >
                Revise the plan
              </Button>
            </div>
          )}

          <section aria-labelledby="care-plan-versions" className="grid gap-3">
            <h3 id="care-plan-versions" className="text-ink font-semibold">
              Versions
            </h3>
            {data.versions
              .filter((version) => version.standing !== 'DRAFT')
              .map((version, index, list) => (
                <VersionCard
                  key={version.id}
                  version={version}
                  previous={list[index + 1] ?? null}
                />
              ))}
          </section>
        </>
      )}
    </div>
  );
}

function VersionCard({
  version,
  previous,
}: {
  version: CarePlanVersionView;
  previous: CarePlanVersionView | null;
}): ReactNode {
  const timeZone = useSession((state) => state.timeZone);
  const standing = STANDING[version.standing];

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-ink font-semibold">Version {version.versionNumber}</h4>
          <p className="text-ink-3 text-sm">
            From {dayText(version.effectiveFrom)}
            {version.approvedByName ? ` · approved by ${version.approvedByName}` : ''}
            {version.approvedAt && timeZone
              ? ` on ${instantText(version.approvedAt, timeZone)}`
              : ''}
          </p>
        </div>
        <StatusPill tone={standing.tone}>{standing.label}</StatusPill>
      </div>
      <VersionBody version={version} />
      {previous && (
        <Changes diff={diffVersions(previous, version)} against={previous.versionNumber} />
      )}
    </Card>
  );
}

function VersionBody({ version }: { version: CarePlanVersionView }): ReactNode {
  return (
    <div className="mt-4 grid gap-3 text-sm">
      {version.goals.length > 0 && (
        <div>
          <Eyebrow>Goals</Eyebrow>
          <ul className="text-ink mt-1 list-disc pl-5">
            {version.goals.map((goal) => (
              <li key={goal.text}>{goal.text}</li>
            ))}
          </ul>
        </div>
      )}
      {version.precautions && (
        <div>
          <Eyebrow>Precautions</Eyebrow>
          <p className="text-ink mt-1">{version.precautions}</p>
        </div>
      )}
      <div>
        <Eyebrow>Tasks</Eyebrow>
        <ul className="mt-1 grid gap-1">
          {version.tasks.map((task) => (
            <li key={task.id} className="border-line rounded-md border px-3 py-2">
              <span className="text-ink font-medium">{task.name}</span>
              <span className="text-ink-3">
                {' '}
                · {daysText(task.daysOfWeek)}
                {task.serviceCode ? ` · ${task.serviceCode} only` : ''}
                {task.isRequired ? ' · required' : ' · optional'}
              </span>
              {task.instructions && (
                <span className="text-ink-2 block text-xs">{task.instructions}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Changes({ diff, against }: { diff: VersionDiff; against: number }): ReactNode {
  const lines = [
    ...diff.added.map((name) => `Added ${name}`),
    ...diff.removed.map((name) => `Removed ${name}`),
    ...diff.changed.map((change) => `Changed ${change.name}: ${change.fields.join(', ')}`),
    ...diff.goalsAdded.map((goal) => `New goal: ${goal}`),
    ...diff.goalsRemoved.map((goal) => `Goal dropped: ${goal}`),
    ...(diff.precautionsChanged ? ['Precautions changed'] : []),
  ];
  return (
    <details className="mt-4 text-sm">
      <summary className="text-ink-2 cursor-pointer font-medium">
        What changed from version {against}
      </summary>
      {lines.length === 0 ? (
        <p className="text-ink-3 mt-2">Nothing — the same tasks, goals and precautions.</p>
      ) : (
        <ul className="text-ink mt-2 list-disc pl-5">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </details>
  );
}

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

interface TaskLine {
  taskLibraryId: string;
  daysOfWeek: number[];
  serviceCodeId: string | null;
  instructions: string;
  isRequired: boolean;
}

function DraftEditor({
  draft,
  today,
  previous,
  library,
  onSaved,
}: {
  draft: CarePlanVersionView;
  today: string;
  previous: CarePlanVersionView | null;
  library: readonly CareTask[];
  onSaved: (plan: CarePlanView) => void;
}): ReactNode {
  const api = useApi();
  const canCreate = useHasPermission('careplan:create');
  const canApprove = useHasPermission('careplan:approve');

  const initial = {
    effectiveFrom: draft.effectiveFrom,
    goals: draft.goals.map((goal) => goal.text),
    precautions: draft.precautions ?? '',
    tasks: draft.tasks.map((task): TaskLine => ({
      taskLibraryId: task.taskLibraryId,
      daysOfWeek: task.daysOfWeek,
      serviceCodeId: task.serviceCodeId,
      instructions: task.instructions ?? '',
      isRequired: task.isRequired,
    })),
  };
  const [form, setForm] = useState(initial);
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState<'save' | 'activate' | 'discard' | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [activated, setActivated] = useState<number | null>(null);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  useUnsavedChangesGuard(dirty);

  const byId = new Map(library.map((task) => [task.id, task]));
  const update = (change: Partial<typeof form>): void => setForm((was) => ({ ...was, ...change }));
  const setTask = (at: number, change: Partial<TaskLine>): void =>
    update({
      tasks: form.tasks.map((task, index) => (index === at ? { ...task, ...change } : task)),
    });

  const body: CarePlanDraftRequest = {
    effectiveFrom: form.effectiveFrom,
    goals: form.goals.filter((goal) => goal.trim()).map((text) => ({ text })),
    precautions: form.precautions.trim() || null,
    tasks: form.tasks.map((task) => ({
      taskLibraryId: task.taskLibraryId,
      daysOfWeek: task.daysOfWeek,
      serviceCodeId: task.serviceCodeId,
      instructions: task.instructions.trim() || null,
      isRequired: task.isRequired,
    })),
  };

  /** The domain's own check, so the button says why before it is pressed. */
  const problem = /^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom)
    ? activationProblem({
        effectiveFrom: toLocalDate(form.effectiveFrom),
        today: toLocalDate(today),
        tasks: form.tasks.map((task) => ({
          name: byId.get(task.taskLibraryId)?.name ?? 'a task',
          requiresDetail: byId.get(task.taskLibraryId)?.requiresDetail ?? false,
          instructions: task.instructions,
        })),
      })
    : 'Choose the day the plan starts.';

  const run = async (kind: 'save' | 'activate' | 'discard'): Promise<void> => {
    setFailure(null);
    if (kind !== 'discard') {
      const parsed = carePlanDraftSchema.safeParse(body);
      if (!parsed.success) {
        setErrors(parsed.error.issues.map((issue) => issue.message));
        return;
      }
    }
    setErrors([]);
    setBusy(kind);
    try {
      if (kind === 'discard') {
        onSaved(await api.send<CarePlanView>('POST', `/v1/care-plan-versions/${draft.id}/discard`));
        return;
      }
      const saved = await api.send<CarePlanView>('PUT', `/v1/care-plan-versions/${draft.id}`, body);
      if (kind === 'save') {
        onSaved(saved);
        return;
      }
      const result = await api.send<CarePlanActivation>(
        'POST',
        `/v1/care-plan-versions/${draft.id}/activate`,
      );
      setActivated(result.visitsUpdated);
      onSaved(result.plan);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(null);
    }
  };

  const unused = library.filter(
    (task) => !form.tasks.some((line) => line.taskLibraryId === task.id),
  );
  const changes = previous
    ? diffVersions(previous, {
        goals: body.goals,
        precautions: body.precautions ?? null,
        tasks: body.tasks.map((task) => ({
          taskLibraryId: task.taskLibraryId,
          serviceCodeId: task.serviceCodeId ?? null,
          name: byId.get(task.taskLibraryId)?.name ?? '',
          daysOfWeek: task.daysOfWeek,
          instructions: task.instructions ?? null,
          isRequired: task.isRequired,
        })),
      })
    : null;

  return (
    <Card>
      <section
        aria-labelledby="care-plan-draft"
        className="grid gap-4"
        data-testid="care-plan-draft"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id="care-plan-draft" className="text-ink font-semibold">
              Version {draft.versionNumber} — draft
            </h3>
            <p className="text-ink-3 text-sm">
              {previous
                ? `A revision of version ${previous.versionNumber}. Nothing changes for caregivers until it is activated.`
                : 'Nothing reaches a visit until it is activated.'}
            </p>
          </div>
          <StatusPill tone="warning">Draft</StatusPill>
        </div>

        {activated !== null && (
          <Notice tone="verified" title="Activated.">
            {activated === 0
              ? 'No booked visits needed new tasks.'
              : `${activated} booked visit${activated === 1 ? '' : 's'} now carry the new tasks. Visits already under way kept theirs.`}
          </Notice>
        )}
        <ErrorNote error={failure} nextStep="Change what the reason names, then try again." />
        {errors.length > 0 && (
          <Notice tone="critical" title="Some of this needs fixing first">
            <ul className="grid gap-1">
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Notice>
        )}

        <Field
          label="Starts on"
          type="date"
          hint="Today or later. A plan never starts in the past: visits already given stay under the plan they were given under."
          min={today}
          value={form.effectiveFrom}
          onChange={(event) => update({ effectiveFrom: event.target.value })}
          disabled={!canCreate}
        />

        <fieldset className="grid gap-2">
          <legend className="text-ink-2 text-sm font-semibold">Goals</legend>
          {form.goals.map((goal, index) => (
            <div key={index} className="flex items-end gap-2">
              <div className="flex-1">
                <Field
                  label={`Goal ${index + 1}`}
                  value={goal}
                  onChange={(event) =>
                    update({
                      goals: form.goals.map((g, at) => (at === index ? event.target.value : g)),
                    })
                  }
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => update({ goals: form.goals.filter((_, at) => at !== index) })}
              >
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => update({ goals: [...form.goals, ''] })}
            >
              Add a goal
            </Button>
          </div>
        </fieldset>

        <Field
          label="Precautions"
          hint="Fall risk, allergies, DNR status, equipment. Every caregiver sees this."
          value={form.precautions}
          onChange={(event) => update({ precautions: event.target.value })}
        />

        <fieldset className="grid gap-3">
          <legend className="text-ink-2 text-sm font-semibold">Tasks</legend>
          {form.tasks.length === 0 && (
            <p className="text-ink-3 text-sm">
              No tasks yet. Add them from the task library below.
            </p>
          )}
          {form.tasks.map((task, index) => {
            const known = byId.get(task.taskLibraryId);
            const name = known?.name ?? 'Task';
            return (
              <div
                key={task.taskLibraryId}
                className="border-line grid gap-2 rounded-md border p-3"
                data-testid="care-plan-task"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-ink font-medium">{name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${name}`}
                    onClick={() => update({ tasks: form.tasks.filter((_, at) => at !== index) })}
                  >
                    Remove
                  </Button>
                </div>
                <div
                  className="flex flex-wrap gap-3"
                  role="group"
                  aria-label={`Days for ${name} (none ticked: every visit)`}
                >
                  {DAY_NAMES.map((day, value) => (
                    <label key={day} className="text-ink flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={task.daysOfWeek.includes(value)}
                        onChange={(event) =>
                          setTask(index, {
                            daysOfWeek: event.target.checked
                              ? [...task.daysOfWeek, value].sort()
                              : task.daysOfWeek.filter((d) => d !== value),
                          })
                        }
                      />
                      {day}
                    </label>
                  ))}
                </div>
                <Field
                  label={`How to do ${name.toLowerCase()}`}
                  hint={known?.requiresDetail ? 'Needed before the plan can start.' : 'Optional.'}
                  value={task.instructions}
                  onChange={(event) => setTask(index, { instructions: event.target.value })}
                />
                <label className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={task.isRequired}
                    onChange={(event) => setTask(index, { isRequired: event.target.checked })}
                  />
                  Required — the caregiver must do it or say why not
                </label>
              </div>
            );
          })}
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-64 flex-1">
              <SelectField
                label="Add a task"
                value={adding}
                onChange={(event) => setAdding(event.target.value)}
              >
                <option value="">Choose from the task library</option>
                {unused.map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.name}
                    {task.isSystem ? '' : ' (this agency’s)'}
                  </option>
                ))}
              </SelectField>
            </div>
            {adding ? (
              <Button
                variant="secondary"
                onClick={() => {
                  update({
                    tasks: [
                      ...form.tasks,
                      {
                        taskLibraryId: adding,
                        daysOfWeek: [],
                        serviceCodeId: null,
                        instructions: '',
                        isRequired: true,
                      },
                    ],
                  });
                  setAdding('');
                }}
              >
                Add
              </Button>
            ) : (
              <Button variant="secondary" disabled disabledReason="Choose a task first.">
                Add
              </Button>
            )}
          </div>
          {canCreate && (
            <AgencyTaskForm
              onCreated={(task) =>
                update({
                  tasks: [
                    ...form.tasks,
                    {
                      taskLibraryId: task.id,
                      daysOfWeek: [],
                      serviceCodeId: null,
                      instructions: '',
                      isRequired: true,
                    },
                  ],
                })
              }
            />
          )}
        </fieldset>

        {changes && <Changes diff={changes} against={previous!.versionNumber} />}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" pending={busy === 'discard'} onClick={() => void run('discard')}>
            Discard the draft
          </Button>
          <Button variant="secondary" pending={busy === 'save'} onClick={() => void run('save')}>
            Save the draft
          </Button>
          {!canApprove ? (
            <Button
              disabled
              disabledReason="Activating a care plan needs careplan:approve. Ask a care manager."
            >
              Activate
            </Button>
          ) : problem ? (
            <Button disabled disabledReason={problem}>
              Activate
            </Button>
          ) : (
            <Button pending={busy === 'activate'} onClick={() => void run('activate')}>
              Activate
            </Button>
          )}
        </div>
        {canApprove && problem && <p className="text-ink-3 text-right text-xs">{problem}</p>}
      </section>
    </Card>
  );
}

const CATEGORY_WORDS: Record<CareTask['category'], string> = {
  ADL: 'Personal care',
  IADL: 'Household',
  SUPERVISION: 'Supervision and company',
  MEDICATION_REMINDER: 'Medication reminder',
  MOBILITY: 'Mobility',
  NUTRITION: 'Nutrition',
  SAFETY: 'Safety',
  OTHER: 'Something else',
};

/**
 * A task the system library does not have — "feed the cat", "check the
 * insulin pump is charged". It joins this agency's library for every client,
 * beside the system tasks, which stay as they are (§6.3.4).
 */
function AgencyTaskForm({ onCreated }: { onCreated: (task: CareTask) => void }): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<CareTask['category']>('OTHER');
  const [requiresDetail, setRequiresDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  if (!open) {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} aria-expanded={false}>
          Not in the library? Add this agency’s own task
        </Button>
      </div>
    );
  }

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);
    try {
      const task = await api.send<CareTask>('POST', '/v1/care-tasks', {
        name: name.trim(),
        category,
        requiresDetail,
      });
      await queryClient.invalidateQueries({ queryKey: ['care-tasks'] });
      onCreated(task);
      setOpen(false);
      setName('');
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-line bg-surface-2 grid gap-3 rounded-md border p-3">
      <ErrorNote error={failure} nextStep="Check the name, then try again." />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Task name" value={name} onChange={(event) => setName(event.target.value)} />
        <SelectField
          label="Kind of task"
          value={category}
          onChange={(event) => setCategory(event.target.value as CareTask['category'])}
        >
          {Object.entries(CATEGORY_WORDS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
      </div>
      <label className="text-ink flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={requiresDetail}
          onChange={(event) => setRequiresDetail(event.target.checked)}
        />
        Always needs instructions on the plan
      </label>
      <div className="flex gap-2">
        {name.trim() ? (
          <Button size="sm" pending={saving} onClick={() => void save()}>
            Add to the library and this plan
          </Button>
        ) : (
          <Button size="sm" disabled disabledReason="Name the task first.">
            Add to the library and this plan
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
